import { GiftDiscountType, GiftPersonaOperation, GiftStatus, GiftType } from "@/api_framework/api_modals/GiftConfig";
import { HomeTileConfigStatus } from "@/api_framework/api_modals/HomeTileConfig";
import { IOrgServiceWithSubServices } from "@/api_framework/api_modals/Org";
import { SelectMenuItems } from "@/app/components/ui/interface";

export interface IGiftIdConditionForm {
  enabled: boolean;
  operation: GiftPersonaOperation;
  min_ids_present: string;
  included_ids: string[];
  excluded_ids: string[];
}

export interface IGiftEligibilityForm {
  persona: IGiftIdConditionForm;
  pincode: IGiftIdConditionForm;
  clinic: IGiftIdConditionForm;
  product: IGiftIdConditionForm;
  category: IGiftIdConditionForm;
  sub_category: IGiftIdConditionForm;
  cart: { enabled: boolean; min_value: string; max_value: string };
  event: { enabled: boolean; is_event_type_gift: boolean; condition_name: string; conditions: string[] };
}

export type GiftIdConditionKey = "persona" | "pincode" | "clinic" | "product" | "category" | "sub_category";

/** Display copy for each include/exclude condition, in the order they are shown. */
export const GIFT_ID_CONDITIONS: {
  key: GiftIdConditionKey;
  title: string;
  description: string;
  itemLabel: string;
  placeholder: string;
}[] = [
  {
    key: "persona",
    title: "Persona Conditions",
    description: "Users with these persona tags.",
    itemLabel: "Personas",
    placeholder: "Type a persona and press Enter",
  },
  {
    key: "pincode",
    title: "Pincode Conditions",
    description: "Delivery pincodes.",
    itemLabel: "Pincodes",
    placeholder: "Type a pincode and press Enter",
  },
  {
    key: "clinic",
    title: "Clinic Conditions",
    description: "Clinics the order is placed for.",
    itemLabel: "Clinics",
    placeholder: "Search clinic by name",
  },
  {
    key: "product",
    title: "Product Conditions",
    description: "Products in the cart.",
    itemLabel: "Products",
    placeholder: "Search product by name (min 3 letters)",
  },
  {
    key: "category",
    title: "Category Conditions",
    description: "Product categories in the cart.",
    itemLabel: "Category IDs",
    placeholder: "Type a category ID and press Enter",
  },
  {
    key: "sub_category",
    title: "Sub Category Conditions",
    description: "Product sub-categories in the cart.",
    itemLabel: "Sub Category IDs",
    placeholder: "Type a sub category ID and press Enter",
  },
];

/** One fixed row per pet species; `enabled` decides whether the row is sent. */
export interface IGiftItemFormRow {
  pet_species_id: string;
  enabled: boolean;
  ids: string[];
}

// Gifts are set per pet species; the app uses species "1" for dogs and "2" for cats.
export const GIFT_PET_SPECIES_ITEMS: SelectMenuItems = [
  { id: "1", title: "Dog" },
  { id: "2", title: "Cat" },
];

export const GIFT_DETAILS_TITLE: Record<GiftType, string> = {
  variant: "Variant Details",
  bom: "BOM Details",
  service: "Service Details",
};

// Variants and services are picked and shown by name; BOM ids are still typed in.
export const GIFT_DETAILS_ID_LABEL: Record<GiftType, string> = {
  variant: "Variant Names",
  bom: "BOM IDs",
  service: "Service Names",
};

/**
 * Flattens the org services VMS loads at startup (staticAPI.org_services) into sub-service options.
 * Service gifts store sub_service_id, which is also what the backend resolves names from.
 */
export const toServiceSelectItems = (services: IOrgServiceWithSubServices[] | null | undefined): SelectMenuItems => {
  const seen = new Set<string>();
  return (services ?? []).flatMap((service) =>
    (service?.sub_services ?? []).flatMap((subService) => {
      const id = String(subService?.sub_service_id ?? "");
      if (!id || seen.has(id)) return [];
      seen.add(id);
      return [
        {
          id,
          title: subService.sub_service_name || id,
          ...(service.service_name && { state: { rightLabel: service.service_name } }),
        },
      ];
    }),
  );
};

// Numeric inputs are kept as strings so an empty field stays empty instead of becoming NaN.
export interface IGiftFormFields {
  gift_name: string;
  gift_tag: string;
  status: GiftStatus;
  start_date: string;
  end_date: string;
  limit_per_user: string;
  total_usage_limit: string;
  max_same_gift_types: string;
  gift_priority: string;
  gift_type: GiftType;
  items: IGiftItemFormRow[];
  coupon_id: string;
  max_quantity_count: string;
  is_auto_addition: boolean;
  discount_type: GiftDiscountType;
  discount_value: string;
  eligibility: IGiftEligibilityForm;
  /** id → name for ids shown by name (clinics, products, gift detail ids); not edited directly. */
  id_names: Record<string, string>;
  tobe_claimed: boolean;
  claim_platform: string;
  allowed_claim_time: string;
  allowed_claim_time_unit: GiftTimeUnit;
  allowed_avail_time: string;
  allowed_avail_time_unit: GiftTimeUnit;
  add_platform: string;
  avail_platform: string;
  heading: string;
  sub_heading: string;
  gift_image: string;
}

export const GIFT_TYPE_LABEL: Record<string, string> = {
  variant: "Product Variant",
  bom: "BOM",
  service: "Service",
};

export const GIFT_TYPE_DETAILS_KEY = {
  variant: "variant_details",
  bom: "bom_details",
  service: "service_details",
} as const satisfies Record<GiftType, string>;

export const isGiftType = (value: string): value is GiftType => value in GIFT_TYPE_DETAILS_KEY;

export type GiftTimeUnit = "days" | "hours" | "minutes";

/** allowed_claim_time / allowed_avail_time are stored in seconds; the form edits them as days, hours or minutes. */
export const GIFT_TIME_UNIT_SECONDS: Record<GiftTimeUnit, number> = {
  days: 86400,
  hours: 3600,
  minutes: 60,
};

export const GIFT_TIME_UNIT_ITEMS: SelectMenuItems = [
  { id: "days", title: "Days" },
  { id: "hours", title: "Hours" },
  { id: "minutes", title: "Minutes" },
];

/** Dropdown items for a `format` list, shown exactly as the API returns them: no renaming, nothing added. */
export const toSelectItems = (values: readonly string[]): SelectMenuItems =>
  values.map((value) => ({ id: value, title: value }));

export type GiftStatusFilter = GiftStatus | "ALL";

export const GIFT_STATUS_FILTERS: { key: GiftStatusFilter; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "Active", label: "Active" },
  { key: "Inactive", label: "InActive" },
];

export const toBadgeStatus = (status: GiftStatus): HomeTileConfigStatus =>
  status === "Active" ? "ACTIVE" : "INACTIVE";

/**
 * Maps the org clinic list to select items titled by clinic name, with the city shown on the right.
 * Callers of this API read the id as either `clinic_id` or `id`, so both are accepted.
 */
export const toClinicSelectItems = (clinics: unknown[]): SelectMenuItems => {
  const seen = new Set<string>();
  return (Array.isArray(clinics) ? clinics : []).flatMap((entry) => {
    const clinic = (entry ?? {}) as Record<string, unknown>;
    const id = String(clinic.clinic_id ?? clinic.id ?? "");
    if (!id || seen.has(id)) return [];
    seen.add(id);
    const name = String(clinic.clinic_name ?? clinic.display_name ?? clinic.name ?? id)
      .split("_")
      .join(" ");
    const city = typeof clinic.city === "string" ? clinic.city : "";
    return [{ id, title: name, ...(city && { state: { rightLabel: city } }) }];
  });
};
